import './Settings.css';

import { useCallback, useEffect, useRef, useState } from 'react';

import WorkQueueListsSettings from './WorkQueueListsSettings';
import axios from 'axios';

interface Recipient {
  upn: string;
  displayName: string;
}

interface NotificationSettingsResponse {
  recipients: Recipient[];
  updatedAt: string | null;
  enabled: boolean;
  deliveryEnabled: boolean;
  emailServiceConfigured: boolean;
}

interface DigestHistoryItem {
  snapshotDate: string;
  /** Which feed the snapshot came from; a date can carry one of each. */
  source: 'xlsx' | 'api';
  status: string;
  trigger: string;
  createdAt: string;
  updatedAt: string;
  subject: string;
  recipients: Recipient[];
  lastError: string | null;
  summary: {
    totals: { providers: number; blocked: number; p1: number; p2: number; p3: number };
    movement: { newlyBlocked: number; newlyAvailable: number };
  };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A date alone is not unique: the weekly and nightly feeds can both report it. */
const digestKey = (item: DigestHistoryItem): string => `${item.snapshotDate}|${item.source}`;

const sourceLabel = (source: DigestHistoryItem['source']): string =>
  source === 'api' ? 'nightly' : 'weekly';

/** Explains what will actually happen, rather than restating the flag. */
function deliveryDescription(settings: NotificationSettingsResponse): string {
  if (!settings.enabled) return 'Digests are turned off entirely. Nothing is built or sent.';
  if (!settings.deliveryEnabled) {
    return 'Shadow mode: the digest is built and recorded after each ingest, but no email is sent. Each one is kept below and can be opened.';
  }
  if (!settings.emailServiceConfigured) {
    return 'Delivery is on, but the WAD email service is not configured, so digests are recorded as blocked instead of sent.';
  }
  return 'Digests are sent after each ingest to the recipients below.';
}

export default function Settings() {
  const [settings, setSettings] = useState<NotificationSettingsResponse | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [history, setHistory] = useState<DigestHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Recipient[]>([]);
  const [searching, setSearching] = useState(false);
  const [manualOnly, setManualOnly] = useState(false);
  const [manualUpn, setManualUpn] = useState('');
  const [manualName, setManualName] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [openSnapshot, setOpenSnapshot] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [settingsResponse, historyResponse] = await Promise.all([
        axios.get<NotificationSettingsResponse>('/api/settings/notifications', { withCredentials: true }),
        // An empty archive is normal before the first ingest, so it must not fail the page.
        axios.get<DigestHistoryItem[]>('/api/settings/digest/history', { withCredentials: true })
          .catch(() => ({ data: [] as DigestHistoryItem[] }))
      ]);
      setSettings(settingsResponse.data);
      setRecipients(settingsResponse.data.recipients);
      setHistory(historyResponse.data);
    } catch {
      setError('Could not load notification settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const search = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await axios.get<Recipient[]>('/api/settings/people/search', {
          params: { q: value.trim() },
          withCredentials: true
        });
        const chosen = new Set(recipients.map((recipient) => recipient.upn));
        setResults(response.data.filter((person) => !chosen.has(person.upn)));
      } catch (searchError: any) {
        // The app may not hold a directory-read permission; manual entry always works.
        if (searchError?.response?.status === 503) setManualOnly(true);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const add = (recipient: Recipient) => {
    if (recipients.some((existing) => existing.upn === recipient.upn)) return;
    setRecipients([...recipients, recipient].sort((left, right) => left.displayName.localeCompare(right.displayName)));
    setQuery('');
    setResults([]);
    setNotice('');
  };

  const addManual = () => {
    const upn = manualUpn.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(upn)) {
      setError('Enter a valid email address.');
      return;
    }
    if (recipients.some((existing) => existing.upn === upn)) {
      setError('That recipient is already in the list.');
      return;
    }
    setError('');
    add({ upn, displayName: manualName.trim() || upn });
    setManualUpn('');
    setManualName('');
  };

  const remove = (upn: string) => {
    setRecipients(recipients.filter((recipient) => recipient.upn !== upn));
    setNotice('');
  };

  // Fetched rather than linked: the dev server's proxy passes navigation
  // requests to the SPA, so opening the endpoint in a tab returns this app
  // instead of the email.
  const openDigest = async (item: DigestHistoryItem) => {
    const key = digestKey(item);
    if (openSnapshot === key) {
      setOpenSnapshot(null);
      setPreviewHtml('');
      return;
    }
    setPreviewLoading(true);
    setError('');
    try {
      // The source is required: a weekly and a nightly digest can share a date,
      // and without it every nightly digest came back as a 404.
      const response = await axios.get<string>(`/api/settings/digest/body/${item.snapshotDate}`, {
        params: { source: item.source },
        withCredentials: true,
        responseType: 'text'
      });
      setPreviewHtml(response.data);
      setOpenSnapshot(key);
    } catch {
      setError('Could not open that digest.');
    } finally {
      setPreviewLoading(false);
    }
  };

  /** Only offered when nothing has been recorded; the archive is the honest view. */
  const previewNext = async () => {
    if (previewHtml) {
      setPreviewHtml('');
      setOpenSnapshot(null);
      return;
    }
    setPreviewLoading(true);
    setError('');
    try {
      const response = await axios.get<string>('/api/settings/digest/preview', {
        withCredentials: true,
        responseType: 'text'
      });
      setPreviewHtml(response.data);
    } catch (previewError: any) {
      setError(
        previewError?.response?.status === 404
          ? 'No snapshot has been ingested yet, so there is nothing to preview.'
          : 'Could not render the digest preview.'
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await axios.put<NotificationSettingsResponse>(
        '/api/settings/notifications',
        { recipients },
        { withCredentials: true }
      );
      setRecipients(response.data.recipients);
      setSettings((current) => (current ? { ...current, ...response.data } : current));
      setNotice('Recipients saved.');
    } catch (saveError: any) {
      setError(saveError?.response?.data?.error || 'Could not save recipients.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="provider-search-container">
        <div className="loading-container"><div className="loading-spinner" /></div>
      </div>
    );
  }

  return (
    <div className="provider-search-container">
      <div className="intro-section">
        <h1>Settings</h1>
        <p className="subtitle">
          The work queue lists, who receives the Epic block digest, and what the digest is currently doing.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <WorkQueueListsSettings />

      {settings && (
        <div className="settings-section">
          <h2 className="settings-heading">Digest delivery</h2>
          <div className={`settings-mode settings-mode-${settings.deliveryEnabled && settings.emailServiceConfigured ? 'live' : 'shadow'}`}>
            <span className="settings-mode-tag">
              {!settings.enabled ? 'Off' : settings.deliveryEnabled ? (settings.emailServiceConfigured ? 'Live' : 'Blocked') : 'Shadow'}
            </span>
            <span>{deliveryDescription(settings)}</span>
          </div>

          <h2 className="settings-heading settings-heading-spaced">
            Digest history
            {history.length > 0 && <span className="settings-count">{history.length}</span>}
          </h2>

          {history.length === 0 ? (
            <>
              <p className="settings-empty">
                No digest has been recorded yet. One is written after the next ingest.
              </p>
              <div className="settings-actions">
                <button className="btn btn-secondary" type="button" onClick={previewNext} disabled={previewLoading}>
                  {previewLoading ? 'Rendering…' : previewHtml ? 'Hide the preview' : 'Preview the next digest'}
                </button>
              </div>
            </>
          ) : (
            <ul className="settings-history">
              {history.map((item) => (
                <li key={digestKey(item)} className={openSnapshot === digestKey(item) ? 'settings-history-open' : ''}>
                  <div className="settings-history-row">
                    <div className="settings-history-main">
                      <div className="settings-digest-head">
                        <strong>{item.snapshotDate}</strong>
                        <span className={`settings-status settings-status-${item.status}`}>{item.status}</span>
                        <span className="settings-history-trigger">{sourceLabel(item.source)}</span>
                        <span className="settings-history-trigger">{item.trigger}</span>
                      </div>
                      <div className="settings-digest-subject">{item.subject}</div>
                      <div className="settings-digest-meta">
                        {item.summary.totals.p1} P1 · {item.summary.totals.blocked} not fully bookable
                        · {item.summary.movement.newlyBlocked} newly blocked
                        · {item.recipients.length} recipient{item.recipients.length === 1 ? '' : 's'}
                        · recorded {new Date(item.updatedAt).toLocaleString()}
                      </div>
                      {item.lastError && <div className="settings-digest-error">{item.lastError}</div>}
                    </div>
                    <button
                      className="btn btn-sm btn-secondary"
                      type="button"
                      onClick={() => openDigest(item)}
                      disabled={previewLoading}
                    >
                      {openSnapshot === digestKey(item) ? 'Hide' : 'Open'}
                    </button>
                  </div>
                  {openSnapshot === digestKey(item) && previewHtml && (
                    <iframe
                      className="settings-preview-frame"
                      title={`Digest for ${item.snapshotDate} (${sourceLabel(item.source)})`}
                      sandbox=""
                      srcDoc={previewHtml}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}

          {history.length === 0 && previewHtml && (
            <iframe
              className="settings-preview-frame"
              title="Digest preview"
              sandbox=""
              srcDoc={previewHtml}
            />
          )}
        </div>
      )}

      <div className="settings-section">
        <h2 className="settings-heading">Recipients</h2>

        {recipients.length === 0 ? (
          <p className="settings-empty">No recipients yet. A digest with no recipients is recorded but cannot be sent.</p>
        ) : (
          <ul className="settings-recipients">
            {recipients.map((recipient) => (
              <li key={recipient.upn}>
                <div>
                  <div className="settings-recipient-name">{recipient.displayName}</div>
                  <div className="settings-recipient-upn">{recipient.upn}</div>
                </div>
                <button className="btn btn-sm btn-secondary" type="button" onClick={() => remove(recipient.upn)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        {!manualOnly && (
          <div className="settings-search">
            <input
              className="settings-input"
              value={query}
              onChange={(event) => search(event.target.value)}
              placeholder="Search the directory by name or email"
              aria-label="Search the directory"
              autoComplete="off"
            />
            {searching && <span className="settings-search-hint">Searching…</span>}
            {results.length > 0 && (
              <ul className="settings-results">
                {results.map((person) => (
                  <li key={person.upn}>
                    <button type="button" onClick={() => add(person)}>
                      <span className="settings-recipient-name">{person.displayName}</span>
                      <span className="settings-recipient-upn">{person.upn}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="settings-manual">
          <div className="settings-manual-label">
            {manualOnly ? 'Directory search is unavailable here, so add addresses directly.' : 'Or add an address directly'}
          </div>
          <div className="settings-manual-row">
            <input
              className="settings-input"
              type="email"
              value={manualUpn}
              onChange={(event) => setManualUpn(event.target.value)}
              placeholder="name@bswhealth.org"
              aria-label="Recipient email address"
              autoComplete="off"
            />
            <input
              className="settings-input"
              value={manualName}
              onChange={(event) => setManualName(event.target.value)}
              placeholder="Display name (optional)"
              aria-label="Recipient display name"
              autoComplete="off"
            />
            <button className="btn btn-secondary" type="button" onClick={addManual}>Add</button>
          </div>
        </div>

        <div className="settings-actions">
          <button className="btn btn-primary" type="button" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save recipients'}
          </button>
          {notice && <span className="settings-notice">{notice}</span>}
          {settings?.updatedAt && (
            <span className="settings-updated">Last changed {new Date(settings.updatedAt).toLocaleString()}</span>
          )}
        </div>
      </div>
    </div>
  );
}
